import { hasValue } from './objectUtils';

export const timestampRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-5]?[0-9])$/;
export const msInOneWeek = 1000 * 60 * 60 * 24 * 7;
export const msInOneDay = 1000 * 60 * 60 * 24;
export const minutesInOneDay = 60 * 24;

export const pad2 = number => (
    number < 10 ? `0${number}` : number
);

// Allow global isNaN to check for Invalid Date object type
// eslint-disable-next-line no-restricted-globals
export const isDateObject = input => input?.getDate && !input.slice && !isNaN(input);

// Get local datetime object from UTC-datetime string
export const getLocalDate = (dateString, inputIsUtc = true) => {
    if (!dateString) {
        return null;
    }

    if (isDateObject(dateString)) {
        return new Date(dateString.getTime()); // dateString is actually Date -> Make copy
    }
    if (dateString.toString() === 'Invalid Date') {
        return null;
    }
    if (!dateString.slice) { // dateString is object or function..
        // eslint-disable-next-line no-console
        console.warn('Date-util: Invalid date object', dateString);
        return null;
    }

    const timeZonestring = inputIsUtc ? 'Z' : '';

    let date;
    if (dateString.slice(-1).toLowerCase() === 'z' || dateString.slice(-6, -5) === '+') {
        // datestring already contains Timezone-info -> create Date object
        date = new Date(dateString);
    } else if (dateString.includes('T')) {
        // full date and time string date. Set UTC timezone
        date = new Date(`${dateString}${timeZonestring}`);
    } else if (dateString.length === 10) { // yyyy-mm-dd
        // only date - Add timestamp and set UTC timezone
        date = new Date(`${dateString}T00:00:00${timeZonestring}`);
    } else if (dateString.length >= 3 && dateString.length <= 5 && dateString.includes(':')) { //
        // only time - Add date and set UTC timezone
        const colonIndex = dateString.indexOf(':');
        const hour = Number(dateString.substring(0, colonIndex));
        const minute = Number(dateString.substring(colonIndex + 1));
        // const timeMatch = dateString.match(timestampRegex); // HH:MM - optional leading zero
        date = new Date(`1970-01-01T${pad2(hour)}:${pad2(minute)}${timeZonestring}`);
    } else {
        // unknown format - trying to parse directly
        date = new Date(`${dateString}${timeZonestring}`);
    }

    if (date && !date.getTime()) { // parsing has failed. invalid date
        // eslint-disable-next-line no-console
        console.warn('Date-util: Parsing datestring failed', dateString);
        return null;
    }

    return date;
};

export const isoDateWithoutTimezone = date => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export const isoDate = date => getLocalDate(date)?.toISOString().substring(0, 10) || '';

export const today = (hour = 0, minute = 0, second = 0) => {
    const d = new Date();
    d.setHours(hour, minute, second, 0);
    return d;
};

export const tomorrow = (hour = 0, minute = 0, second = 0) => {
    const d = new Date();
    d.setHours(hour, minute, second, 0);
    d.setDate(d.getDate() + 1);
    return d;
};

export const yesterday = (hour = 0, minute = 0, second = 0) => {
    const d = new Date();
    d.setHours(hour, minute, second, 0);
    d.setDate(d.getDate() - 1);
    return d;
};

export const isSameDate = (datetime1, datetime2) => {
    if (!datetime1 || !datetime2) return false;
    const d1 = getLocalDate(datetime1);
    const d2 = getLocalDate(datetime2);

    return d1.toLocaleDateString() === d2.toLocaleDateString();
};

export const isToday = dateString => isSameDate(dateString, today());

export const isYesterday = dateString => isSameDate(dateString, yesterday());

export const isFuture = (dateString, includeToday) => {
    const date = getLocalDate(dateString);

    if (!date) {
        return false;
    }

    if (includeToday) {
        return date.getTime() >= today().getTime();
    }

    return date.getTime() > Date.now();
};

export const addDays = (initialDate, days) => {
    const date = initialDate ? new Date(initialDate) : new Date();
    date.setDate(date.getDate() + days);
    return date;
};

export const addMonths = (initialDate, months) => {
    const date = initialDate ? new Date(initialDate) : new Date();
    date.setMonth(date.getMonth() + months);
    return date;
};

export const fullDateTime = dateString => getLocalDate(dateString)?.toString();

export const timeOnly = (dateOrString, translate) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return null;
    }
    return `${translate ? `${translate('time.at')} ` : ''}${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const getTimePartForInput = (datetime) => {
    if (!datetime) {
        return '';
    }
    if (datetime.length === 5 && datetime[2] === ':') {
        return datetime;
    }

    const date = new Date(datetime);
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const hasTime = (dateOrString) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return false;
    }

    return date.getHours() !== 0 || date.getMinutes() !== 0;
};

export const longTimestamp = (dateOrString) => {
    const d = dateOrString ? getLocalDate(dateOrString) : new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}-${pad2(d.getMinutes())}.${pad2(d.getSeconds())}`;
};

// Get date or time-string from UTC-datetime string
export const timestamp = (dateOrString, translate) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return null;
    }

    if (isToday(date)) {
        if (translate && date.getHours() === 0 && date.getMinutes() === 0) {
            return translate('time.today');
        }
        return `${translate ? translate('time.todayAt') : ''} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    }
    if (isYesterday(date)) {
        if (translate && date.getHours() === 0 && date.getMinutes() === 0) {
            return translate('time.yesterday');
        }
        return `${translate ? translate('time.yesterdayAt') : `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}`} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    }
    if (date.getFullYear() === new Date().getFullYear()) {
        if (date.getHours() === 0 && date.getMinutes() === 0) {
            return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}`;
        }
        return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)} ${translate ? translate('time.at') : ''} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    }
    if (date.getHours() === 0 && date.getMinutes() === 0) {
        return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
    }
    return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()} ${translate ? translate('time.at') : ''} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const datestamp = (dateOrString) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return '';
    }

    return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export const longDate = (dateOrString, translate, alwaysIncludeYear = false) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return null;
    }

    const year = alwaysIncludeYear || date.getFullYear() !== new Date().getFullYear() ? ` ${date.getFullYear()}` : '';

    return `${date.getDate()}. ${translate(`time.months.${date.getMonth()}`)}${year}`;
};

export const shortDate = (dateOrString, translate, includeYear = false, withDayName = false, alwaysDate = false) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return null;
    }

    const year = includeYear ? ` ${date.getFullYear()}` : '';

    if (translate) {
        if (!alwaysDate) {
            if (isToday(date)) {
                return translate('time.today');
            }
            if (isToday(addDays(date, 1))) {
                return translate('time.yesterday');
            }
            if (isToday(addDays(date, -1))) {
                return translate('time.tomorrow');
            }
        }

        const dayName = withDayName ? `${translate(`time.weekdays_short.${date.getDay()}`)} ` : '';

        return `${dayName}${date.getDate()}. ${translate(`time.months_short.${date.getMonth()}`)}${year}`;
    }

    return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}${year}`;
};

export const mondayZeroIndex = n => (((n - 1) % 7) + 7) % 7;

export const getTimeStringFromDayOfWeek = (day) => {
    const a = new Date();
    const dayOffset = (a.getDay() - day) * 1000 * 60 * 60 * 24 * -1;
    const d = new Date(a.getTime() + dayOffset);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
};

export const dateWithOptionalTime = (dateOrString) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return null;
    }

    if (date.getHours() === 0 && date.getMinutes() === 0) {
        return date.toLocaleDateString();
    }
    return date.toLocaleString().slice(0, -3);
};

export const dateWithTimeIfToday = (dateOrString) => {
    const date = getLocalDate(dateOrString);

    if (!date) {
        return null;
    }

    if (isToday(date)) {
        return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    }
    return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
};

// returns string to set in input type=date
export const getTextfieldDateString = (dateOrString) => {
    const d = dateOrString ? getLocalDate(dateOrString) : new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// returns string to set in input type=datetime-local
export const getTextfieldDateTimeString = (dateOrString, setNoon = false) => {
    const d = dateOrString ? getLocalDate(dateOrString) : new Date();
    if (setNoon) {
        d.setHours(12, 0, 0, 0);
    }
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// returns string to save in database
export const getDateTimeSaveString = (date) => {
    let d;
    if (!date) {
        return null;
    }

    if (date.length === 10) {
        d = new Date(`${date}T00:00`);
    } else {
        d = new Date(date);
    }
    return d.toISOString();
};

export const getWeekNumberFromDate = (dateIn) => {
    const date = dateIn ? new Date(Date.UTC(dateIn.getFullYear(), dateIn.getMonth(), dateIn.getDate())) : new Date();

    // Thursday in current week decides the year.
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    // January 4 is always in week 1.
    const week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from date to week1.
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

export const getWeekNumber = (dateString) => {
    const date = getLocalDate(dateString);

    return getWeekNumberFromDate(date);
};

export const getNumberOfWeeks = (startTime, endTime) => {
    if (!startTime || !endTime) {
        return 0;
    }

    return Math.floor(Math.abs(new Date(startTime) - new Date(endTime)) / msInOneWeek) || 0;
};

export const getNumberOfWeeksCeiled = (startTime, endTime) => {
    if (!startTime || !endTime) {
        return 0;
    }

    return Math.ceil(Math.abs(new Date(startTime) - new Date(endTime)) / msInOneWeek) || 0;
};

export const getNumberOfDays = (startTime, endTime) => {
    if (!startTime || !endTime) {
        return 0;
    }

    return Math.floor(Math.abs(new Date(startTime) - new Date(endTime)) / msInOneDay);
};

export const getWeekStartDate = (weekNumber, year) => {
    const weekYear = year || new Date().getFullYear();
    const simple = new Date(weekYear, 0, 1 + (weekNumber - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    return ISOweekStart;
};

export const getNumberOfSeconds = (startTime, endTime) => {
    if (!startTime || !endTime) {
        return 0;
    }

    return Math.floor(Math.abs(new Date(startTime) - new Date(endTime)) / 1000);
};

export const getNumberOfMinutes = (startTime, endTime) => getNumberOfSeconds(startTime, endTime) / 60;

export const getDatePart = (dateOrString) => {
    const d = getLocalDate(dateOrString);
    d.setHours(0, 0, 0, 0);
    return d;
};

export const compareDates = (date, daysToAddForCompare = 0) => {
    const date1 = getLocalDate(date) || { getTime: () => 0 };
    return date1.getTime() > addDays(Date.now(), daysToAddForCompare).getTime();
};

export const getDateFormatted = (dateOrString, translateFunc) => {
    const d = getLocalDate(dateOrString);
    return `${translateFunc(`calendar.shortMonth${d.getMonth() + 1}`)}. ${d.getDate()}`;
};

export const getMinsToMidnight = () => {
    const d = new Date();
    return (-d + d.setHours(24, 0, 0, 0)) / 6e4;
};

export const getMillisecondsInADay = () => 1000 * 60 * 60 * 24;

export const hourCount = (minutes, translate, longLabels = false) => {
    if (!hasValue(minutes) || Number.isNaN(minutes) || minutes === 'NaN') return '';
    const minLabel = translate ? translate(longLabels ? 'time.minutes' : 'time.minutesShort') : 'm';
    if (minutes < 60) return `${Math.round(minutes % 60)} ${minLabel}`;

    const hours = Math.floor(minutes / 60);
    const hourLabel = translate ? translate(longLabels ? hours > 1 ? 'time.hours' : 'time.hour' : 'time.hoursShort') : 'h';
    if (minutes % 60 === 0) return `${hours} ${hourLabel}`;
    return `${hours} ${hourLabel} ${Math.round(minutes % 60)} ${minLabel}`;
};

export const getTimeValues = minutes => ({
    minutes: (minutes % 60) || 0,
    hours: Math.floor(minutes / 60) || 0,
});

// export const getWeek = (startDate?: Date): Week => {
export const getWeek = (startDate) => {
    const start = startDate ?? new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + (-start.getDay() + 1));
    const end = addDays(start, 6);
    end.setHours(23, 59, 59);

    return {
        start,
        // lastDate: addDays(end, -1), // end is date after..
        end,
        weekNo: getWeekNumberFromDate(start),
    };
};

export const getAge = (dateString) => {
    if (!dateString) return null;

    const todayDate = new Date();
    const birthDate = getLocalDate(dateString);
    let age = todayDate.getFullYear() - birthDate.getFullYear();
    const m = todayDate.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && todayDate.getDate() < birthDate.getDate())) {
        age -= 1;
    }
    return age;
};

export const sortDatesByTimeInAscendingOrder = (last, curr) => {
    let result;

    result = last.getHours() > curr.getHours() ? 1 : last.getHours() < curr.getHours() ? -1 : 0;

    // Compares minutes if hours are identical.
    if (result === 0) result = last.getMinutes() > curr.getMinutes() ? 1 : last.getMinutes() < curr.getMinutes() ? -1 : 0;

    // Compares seconds if minutes are identical.
    if (result === 0) result = last.getSeconds() > curr.getSeconds() ? 1 : last.getSeconds() < curr.getSeconds() ? -1 : 0;

    return result;
};
